import React from 'react';
import { CalendarDaysIcon, MapPinIcon } from 'lucide-react';
import { FloatingPhoto } from '../motion/FloatingPhoto';
import { Reveal } from '../motion/Reveal';
import { Container, Eyebrow, Section } from '../ui/Section';
import { ButtonExternal, ButtonLink } from '../ui/Button';
import { PhotoFrame } from '../ui/PhotoFrame';
import { DoodleArrow, DoodleSquiggle, DoodleStar, DoodleUnderline } from '../brand/Doodles';
import { DoodleScatter } from '../brand/DoodleScatter';
import { externalLinks, meeting } from '../../data/site';
import { heroPhotos } from '../../data/photos';
export function Hero() {
  return <Section className="relative overflow-hidden bg-[#F4F7FB] pb-16 pt-12 sm:pb-20 sm:pt-16" labelledBy="hero-heading" data-id="element-126" data-mp="rf8clkkmzbz">
      {/* Cool near-white hero: scatter uses navy, yellow, and light blue. */}
      <DoodleScatter tone="coolLight" seed="hero-who-we-are" data-id="element-127" data-mp="1v33nooupt3ww" />
      <Container className="relative grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-14" data-id="element-128" data-mp="1q4shqp7jjdjy">
        <Reveal data-id="element-129" data-mp="1mvt35l9go6yw">
          <Eyebrow data-id="element-130" data-mp="1mvaz42gk4ogf">Baptist Collegiate Ministries at UCF</Eyebrow>

          <h1 id="hero-heading" className="mt-4 font-display text-[2.4rem] font-extrabold leading-[1.05] tracking-tight text-brand-navy sm:text-5xl lg:text-[3.6rem]" data-id="element-131" data-mp="1rv5qe748vg20">
            <span className="relative inline-block" data-id="element-132" data-mp="1hwzo3oej07wm">
              Who We Are
              <DoodleUnderline className="absolute -bottom-2 left-0 h-3 w-full sm:-bottom-3 sm:h-4" color="var(--bcm-yellow)" data-id="element-133" data-mp="1hz2o919e7hf2" />
            </span>
          </h1>

          <p className="mt-7 max-w-[58ch] font-sans text-[17px] leading-relaxed text-ink sm:text-lg" data-id="element-134" data-mp="1vx0qx9ecppeq">
            BCM stands for Baptist Collegiate Ministries, but you don’t need to be a Baptist, or even
            a Christian, to find a home here. Our students come from all kinds of backgrounds. We
            receive generous support from churches in the Florida Baptist Convention, and we’re
            thankful for it! Those churches–over 2800 of them across the state of Florida–and the
            people who faithfully contribute to them, have committed to invest in this ministry, and
            in you! Everyone is welcome!
          </p>

          <div className="relative mt-9 w-fit" data-id="element-135" data-mp="2u6dtwcaijjuo">
            {/* -left-5 keeps the star inside the container's px-5 gutter on phones. */}
            <DoodleStar className="pointer-events-none absolute -left-5 -top-5 h-7 w-7 sm:-left-9 sm:h-8 sm:w-8" color="var(--bcm-yellow)" data-id="element-136" data-mp="2payirs3w421y" />
            <DoodleSquiggle className="pointer-events-none absolute -right-10 -top-4 hidden h-6 w-20 sm:block" color="var(--bcm-dark-blue)" data-id="element-137" data-mp="2fhhraur86z1g" />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-138" data-mp="26jpafm5uvjby">
              <ButtonExternal href={externalLinks.getConnectedForm} className="min-h-[56px] px-8 py-4 text-[17px] sm:text-lg" data-id="element-139" data-mp="21hzx1zv5utkp">
                Get Connected
              </ButtonExternal>
              <ButtonLink to="/about" variant="secondary" className="min-h-[56px] px-7" data-id="element-140" data-mp="c1nypxfd1oct">
                Learn More About BCM
              </ButtonLink>
            </div>
            <DoodleUnderline className="pointer-events-none absolute -bottom-3 left-4 h-2.5 w-[38%]" color="var(--bcm-yellow)" data-id="element-141" data-mp="m151o7389o53" />
          </div>

          <div className="mt-12 max-w-lg rounded-[20px] border-2 border-brand-navy bg-white p-5" data-id="element-142" data-mp="3phljn3xbs43q">
            <p className="flex items-start gap-2.5 font-meta text-sm font-semibold text-brand-navy" data-id="element-143" data-mp="3uf3w34ajx1gh">
              <CalendarDaysIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" data-id="element-144" data-mp="2buiuk86qn2r" />
              {meeting.summary}
            </p>
            <div className="mt-2.5 flex items-start gap-2.5 font-meta text-sm text-ink" data-id="element-145" data-mp="7446ftltvb1v">
              <MapPinIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-146" data-mp="36pt0s2byzz9q" />
              <p data-id="element-147" data-mp="3aqd09ck0nez1">
                {meeting.locationLines.map(line => <span key={line} className="block" data-id="element-148" data-mp="2rocgt3l7bphg">
                    {line}
                  </span>)}
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal x={18} y={16} delay={0.1} className="relative" data-id="element-149" data-mp="31dcgyqbfwqx8">
          <DoodleStar className="absolute -left-3 -top-4 z-10 h-9 w-9 sm:-left-6 sm:h-12 sm:w-12" color="var(--bcm-yellow)" data-id="element-150" data-mp="31ex9kae71zsg" />
          <DoodleArrow className="absolute -bottom-6 -left-2 z-10 hidden h-16 w-16 lg:block" color="var(--bcm-yellow)" data-id="element-151" data-mp="3achj06m7n2e5" />
          <DoodleSquiggle className="absolute -right-2 -top-7 z-10 h-8 w-28 sm:-right-6 sm:h-10 sm:w-36" color="var(--bcm-dark-blue)" data-id="element-152" data-mp="3kboqwea6e4tv" />

          <div className="grid grid-cols-5 gap-3 sm:gap-4" data-id="element-153" data-mp="3fasne1tqkkoz">
            {/* 4:3 keeps landscape group shots intact instead of cropping them
                to a portrait sliver. */}
            <FloatingPhoto className="col-span-5 sm:col-span-3" range={7} duration={7.5} data-id="element-154" data-mp="1l0p1fku67t0c">
              <PhotoFrame photo={heroPhotos[0]} ratio="aspect-[4/3]" rotate="-rotate-1" className="transition-transform duration-300 hover:scale-[1.015]" data-id="element-155" data-mp="hbuvuakainh" />
            </FloatingPhoto>
            <div className="col-span-5 flex flex-row gap-3 sm:col-span-2 sm:flex-col sm:gap-4" data-id="element-156" data-mp="3ruxzw6ers1ie">
              <FloatingPhoto className="min-w-0 flex-1" range={5} duration={6} delay={0.8} data-id="element-157" data-mp="3dv6u2syeprfh">
                <PhotoFrame photo={heroPhotos[1]} ratio="aspect-square" rotate="rotate-2" className="transition-transform duration-300 hover:scale-[1.015]" data-id="element-158" data-mp="3hx1vdxgc84t9" />
              </FloatingPhoto>
              <FloatingPhoto className="min-w-0 flex-1" range={4} duration={8.5} delay={0.3} data-id="element-159" data-mp="38ygdm4iowttj">
                <PhotoFrame photo={heroPhotos[2]} ratio="aspect-square" rotate="-rotate-2" className="transition-transform duration-300 hover:scale-[1.015]" data-id="element-160" data-mp="1242xe9z9jin1" />
              </FloatingPhoto>
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>;
}